import { MediaAssetProviders } from '@/features/ai-video-creator/stores/productMediaSlice';
import useRouterNavigation from '@/hooks/useRouterNavigation';
import { ICardMediaAsset, MediaAssetPurpose, MediaAssetType } from '@/types/media';
import StudioRoutes from '@/utils/routes';
import { Popover, Button, ActionList, InlineStack, ActionListItemProps, ButtonProps, Box } from '@shopify/polaris';
import {
  MenuVerticalIcon,
  PlayCircleIcon,
  CameraFlipIcon,
  EditIcon,
  DeleteIcon,
  PersonalizedTextIcon,
} from '@shopify/polaris-icons';
import { useState, useMemo, useCallback } from 'react';
import { RenameMediaAssetModal } from './RenameMediaAssetModal';
import { DeleteMediaAssetModal } from './DeleteMediaAssetModal';
import { Avatar } from '@/types/avatar';
import { PersonalAvatarViewData } from '@/api/personal-avatars';
import { useMediaInStudioVideo } from '@/hooks/useMediaInStudioVideo';
import { ANIMATE_IMAGE, PRODUCT_TRY_ON } from '@/constants/text';

type MediaAssetCardQuickViewActionsProps = {
  media: ICardMediaAsset | Avatar | PersonalAvatarViewData;
  disabled: boolean;
  extractPrimaryAction?: boolean;
  buttonSize?: ButtonProps['size'];
};

const MediaAssetCardQuickViewActions = ({
  media,
  disabled,
  extractPrimaryAction = false,
  buttonSize = 'large',
}: MediaAssetCardQuickViewActionsProps) => {
  const { pushPath } = useRouterNavigation();

  const [popoverActive, setPopoverActive] = useState(false);
  const [isRenameModalOpen, setIsRenameModalOpen] = useState(false);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);

  const togglePopoverActive = useCallback(() => setPopoverActive((prev) => !prev), [setPopoverActive]);

  const { handleUseInStudioVideo } = useMediaInStudioVideo({ media });

  const handleAnimateImage = useCallback(() => {
    const asset = media as ICardMediaAsset;
    pushPath(
      StudioRoutes.animateImage({
        assetId: asset.id,
        assetSrc: asset.src || '',
        assetType: asset.type || MediaAssetType.image,
        assetProvider: asset.source as MediaAssetProviders,
      })
    );
  }, [media, pushPath]);

  const handleProductTryOn = useCallback(() => {
    const asset = media as ICardMediaAsset;
    pushPath(
      StudioRoutes.productTryOn({
        assetId: asset.id,
        assetSrc: asset.src || '',
        assetType: asset.type || MediaAssetType.image,
        assetProvider: asset.source as MediaAssetProviders,
      })
    );
  }, [media, pushPath]);

  const items = useMemo(
    () => [
      ...((media as Avatar)
        ? [
            {
              content: 'Use',
              icon: PlayCircleIcon,
              onAction: handleUseInStudioVideo,
            },
          ]
        : []),
      ...(media.type === MediaAssetType.image
        ? [
            {
              content: ANIMATE_IMAGE,
              icon: CameraFlipIcon,
              onAction: handleAnimateImage,
            },
          ]
        : []),
      ...(media.type === MediaAssetType.image && media.purpose === MediaAssetPurpose.tryOnAvatar
        ? [
            {
              content: PRODUCT_TRY_ON,
              icon: PersonalizedTextIcon,
              onAction: handleProductTryOn,
            },
          ]
        : []),
      ...(!(media as Avatar).hardcoded
        ? [
            {
              content: 'Rename',
              icon: EditIcon,
              onAction: () => setIsRenameModalOpen(true),
            },
            {
              content: 'Delete',
              icon: DeleteIcon,
              onAction: () => setIsDeleteModalOpen(true),
            },
          ]
        : []),
    ],
    [media, handleAnimateImage, handleUseInStudioVideo, setIsDeleteModalOpen, setIsRenameModalOpen]
  );

  const renderPrimaryAction = useCallback(
    (item: ActionListItemProps) => (
      <Box>
        <Button onClick={item.onAction} icon={item.icon} disabled={disabled} size={buttonSize}>
          {item.content}
        </Button>
      </Box>
    ),
    [disabled]
  );

  const renderPopover = useCallback(
    (items: ActionListItemProps[]) => (
      <Popover
        active={popoverActive}
        activator={
          <Button onClick={togglePopoverActive} icon={MenuVerticalIcon} size={buttonSize} disabled={disabled} />
        }
        onClose={togglePopoverActive}
      >
        <Popover.Pane>
          <ActionList actionRole="menuitem" items={items} />
        </Popover.Pane>
      </Popover>
    ),
    [popoverActive, disabled, togglePopoverActive]
  );

  const content = useMemo(() => {
    if (items.length === 1) return renderPrimaryAction(items[0]);
    if (extractPrimaryAction) {
      return (
        <InlineStack align="space-between">
          {renderPrimaryAction(items[0])}
          {renderPopover(items.slice(1))}
        </InlineStack>
      );
    }
    return renderPopover(items);
  }, [items, popoverActive, disabled, togglePopoverActive]);

  return (
    <>
      {content}
      <DeleteMediaAssetModal mediaAsset={media} isOpen={isDeleteModalOpen} setIsOpen={setIsDeleteModalOpen} />
      <RenameMediaAssetModal mediaAsset={media} isOpen={isRenameModalOpen} setIsOpen={setIsRenameModalOpen} />
    </>
  );
};

export default MediaAssetCardQuickViewActions;
